import {
  GridFilterModel,
  GridLogicOperator,
  GridSortDirection,
  GridSortModel,
} from '@mui/x-data-grid-premium'
import { RawDataGridTableSettings } from 'components/DataGrid/dataGridTableSettingsAdapter'
import { DataGridSavedReportConfig, DataGridSavedReportConfigInput } from 'generated/graphql'


function requireSetting<T>(
  value: T | null,
  field: keyof RawDataGridTableSettings
): T {
  if (value === null) {
    throw new Error(
      `Cannot serialize DataGrid saved report: ${field} is missing`
    )
  }

  return value
}

function serializeSortModel(
  sortModel: GridSortModel
): DataGridSavedReportConfigInput['sortModel'] {
  return sortModel.map(({ field, sort }) => {
    if (sort == null) {
      throw new Error(
        `Cannot serialize DataGrid saved report: sort direction is missing for ${field}`
      )
    }

    return {
      field,
      sort,
    }
  })
}

function serializeFilterModel(
  filterModel: GridFilterModel
): DataGridSavedReportConfigInput['filterModel'] {
  return {
    items: filterModel.items.map(
      ({ field, id, operator, value }) => ({
        field,
        operator,
        ...(id !== undefined ? { id } : {}),
        ...(value !== undefined ? { value } : {}),
      })
    ),
    ...(filterModel.logicOperator !== undefined
      ? { logicOperator: filterModel.logicOperator }
      : {}),
  }
}

export function serializeDataGridSavedReportConfig(
  settings: RawDataGridTableSettings
): DataGridSavedReportConfigInput {
  const paginationModel = requireSetting(
    settings.paginationModel,
    'paginationModel'
  )
  const sortModel = requireSetting(
    settings.sortModel,
    'sortModel'
  )
  const filterModel = requireSetting(
    settings.filterModel,
    'filterModel'
  )
  const columnOrder = requireSetting(
    settings.columnOrder,
    'columnOrder'
  )
  const columnVisibilityModel = requireSetting(
    settings.columnVisibilityModel,
    'columnVisibilityModel'
  )

  return {
    paginationModel: {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    },
    sortModel: serializeSortModel(sortModel),
    filterModel: serializeFilterModel(filterModel),
    columnOrder: [...columnOrder],
    columnVisibilityModel: {
      ...columnVisibilityModel,
    },
  }
}

function deserializeSortModel(
  sortModel: DataGridSavedReportConfig['sortModel']
): GridSortModel {
  return sortModel.map(({ field, sort }) => ({
    field,
    sort: sort as GridSortDirection,
  }))
}

function deserializeFilterModel(
  filterModel: DataGridSavedReportConfig['filterModel']
): GridFilterModel {
  return {
    items: filterModel.items.map(
      ({ field, id, operator, value }) => ({
        field,
        operator,
        ...(id != null ? { id } : {}),
        ...(value != null ? { value } : {}),
      })
    ),
    ...(filterModel.logicOperator != null
      ? {
          logicOperator:
            filterModel.logicOperator as GridLogicOperator,
        }
      : {}),
  }
}

export function deserializeDataGridSavedReportConfig(
  config: DataGridSavedReportConfig
): RawDataGridTableSettings {
  return {
    paginationModel: {
      page: config.paginationModel.page,
      pageSize: config.paginationModel.pageSize,
    },
    sortModel: deserializeSortModel(config.sortModel),
    filterModel: deserializeFilterModel(config.filterModel),
    columnOrder: [...config.columnOrder],
    columnVisibilityModel: {
      ...config.columnVisibilityModel,
    },
    savedReportId: null,
    savedReportName: null,
  }
}
